// ===========================================
// Calendar Page Helpers
// ===========================================

import { format } from 'date-fns';
import { CalendarEvent, getEventTypeColor } from '../../../api/hybridBooking.api';
import { getContrastTextColor } from '../../../styles/brandColors';
import {
  CalendarDisplaySettings,
  ColorSource,
  BorderColorSource,
  TeacherNameFormat,
  formatTeacherName,
} from '../CalendarDisplaySettingsModal';
import { DRAGGABLE_TYPES } from './calendar.constants';
import { toLocalDateString, toUTCDateKey } from '../../../utils/dateUtils';

/**
 * "Room A (Mosman)" — a room's display title with its location, used by the
 * calendar's room view and the extra-lesson room picker. Reads `Location`
 * (PascalCase, as `getRooms` sends it); the old `room.location` read never matched,
 * so rooms showed with no location.
 */
export const roomResourceTitle = (room: { name: string; Location?: { name: string } | null }): string =>
  room.Location?.name ? `${room.name} (${room.Location.name})` : room.name;

/**
 * Format time as HH:mm for API calls
 */
export const formatTimeForApi = (date: Date): string => {
  return format(date, 'HH:mm');
};

/**
 * The calendar-day key identifying WHICH occurrence a drag/cancel acts on.
 *
 * `LessonException.originalDate` is an identity key (`@@unique([lessonId, originalDate])`),
 * so this must be the value the backend stored, never one recomputed from a rendered Date.
 *
 * Order matters and is load-bearing. For a RESCHEDULED occurrence the backend sets `dateKey`
 * to the NEW date and `resource.originalDate` to the ORIGINAL
 * (`hybridBooking.calendar.ts:1234` vs `:1258`) — they deliberately differ. Checking
 * `resource.originalDate` FIRST is what keeps a re-drag pinned to the same exception row
 * instead of creating a second one. Every other state (normal / cancelled / teacher-replaced)
 * omits `resource.originalDate`, so `dateKey` is correct there.
 *
 * ⚠️ The last branch is UNREACHABLE for draggable lesson events — all four `DRAGGABLE_TYPES`
 * emit `dateKey` unconditionally. It is NOT equivalent to `reconstructEventDates`'s fallback:
 * that one parses a UTC-origin ISO string off the wire, whereas by the time an event reaches
 * here `event.start` has already been rebuilt as a LOCAL-anchored Date, so LOCAL getters are
 * the correct reading. Kept only so the function is total.
 */
export const originalDateKeyForEvent = (event: CalendarEvent): string => {
  if (event.resource?.originalDate) return toUTCDateKey(event.resource.originalDate);
  if (event.dateKey) return toUTCDateKey(event.dateKey);
  return toLocalDateString(new Date(event.start));
};

/**
 * Build the single-occurrence reschedule/conflict-check payload from a drop or resize.
 *
 * This exists as a named, exported function for one reason: the customer bug was never in the
 * date helpers — those were already correct — it was in the CALL SITES choosing the wrong one.
 * Testing the helpers in isolation cannot catch that (verified: restoring the bug at every call
 * site left 111/111 tests green). Every site now routes through here, so a single test pins all
 * of them.
 *
 * The invariant: `newDate` and `newStartTime` describe the SAME instant and must be read in the
 * SAME frame. `newStart` is a LOCAL-anchored Date from react-big-calendar, so both are local.
 * `originalDate` is an identity key and is passed through, never re-derived.
 */
export const buildOccurrencePayload = (
  event: CalendarEvent,
  newStart: Date,
  newEnd: Date
): {
  originalDate: string;
  newDate: string;
  newStartTime: string;
  newEndTime: string;
} => ({
  originalDate: originalDateKeyForEvent(event),
  newDate: toLocalDateString(newStart),
  newStartTime: formatTimeForApi(newStart),
  newEndTime: formatTimeForApi(newEnd),
});

/**
 * Get color for a source type
 */
export const getColorForSource = (
  source: ColorSource | BorderColorSource,
  event: CalendarEvent
): string | undefined => {
  switch (source) {
    case 'lessonType':
      return event.resource?.lessonTypeColor;
    case 'teacher':
      return event.resource?.teacherColor;
    case 'instrument':
      return event.resource?.instrumentColor;
    case 'programLevel':
      return event.resource?.programLevelColor;
    case 'stream':
      return event.resource?.streamColor;
    case 'none':
      return undefined;
    default:
      return undefined;
  }
};

/**
 * Apply display settings label to an event
 */
export const applyEventLabel = (
  event: CalendarEvent,
  labelSource: CalendarDisplaySettings['labelSource'],
  teacherNameFormat?: TeacherNameFormat
): CalendarEvent => {
  // Don't override special event types
  if (
    event.resource?.type === 'MEET_AND_GREET' ||
    event.resource?.type === 'HYBRID_PLACEHOLDER' ||
    event.resource?.type === 'EXTRA_LESSON' ||
    event.resource?.type === 'MAKEUP' ||
    event.resource?.type === 'EVENT' ||
    event.resource?.isBooking
  ) {
    return event;
  }

  let newTitle = event.title;
  switch (labelSource) {
    case 'lessonName':
      newTitle = event.resource?.lessonName || event.title;
      break;
    case 'teacherName':
      newTitle = event.resource?.teacherName
        ? formatTeacherName(event.resource.teacherName, teacherNameFormat)
        : event.title;
      break;
    case 'instrumentName':
      newTitle = event.resource?.instrumentName || event.title;
      break;
    case 'roomName':
      newTitle = event.resource?.roomName || event.title;
      break;
    case 'programLevelName':
      newTitle = event.resource?.programLevelName || event.title;
      break;
  }
  return { ...event, title: newTitle };
};

/**
 * Get event style based on display settings
 */
export const getEventStyle = (
  event: CalendarEvent,
  displaySettings: CalendarDisplaySettings
) => {
  const type = event.resource?.type || 'GROUP';
  const defaultColor = getEventTypeColor(type, displaySettings.specialEventColors);
  const isPlaceholder = type === 'HYBRID_PLACEHOLDER';
  const isDraggable = DRAGGABLE_TYPES.includes(type);

  // Special event types use their designated colors unless the admin has toggled "use background color"
  const isSpecialEventType = ['HYBRID_GROUP', 'HYBRID_INDIVIDUAL', 'HYBRID_PLACEHOLDER', 'MEET_AND_GREET', 'EXTRA_LESSON', 'MAKEUP', 'EVENT'].includes(type);

  // Map event type to override key
  const overrideKey: Record<string, keyof NonNullable<typeof displaySettings.specialEventColorOverrides>> = {
    HYBRID_GROUP: 'hybridGroup',
    HYBRID_INDIVIDUAL: 'hybridIndividual',
    HYBRID_PLACEHOLDER: 'hybridPlaceholder',
    MEET_AND_GREET: 'meetAndGreet',
  };
  const useBackgroundColor = isSpecialEventType && !!displaySettings.specialEventColorOverrides?.[overrideKey[type]];

  let backgroundColor: string;
  if (isSpecialEventType && !useBackgroundColor) {
    // Special event types use their special colors (default behaviour)
    backgroundColor = defaultColor;
  } else {
    // Regular lessons, or special types with "use background color" toggled on
    const backgroundFromSettings = getColorForSource(
      displaySettings.backgroundColorSource,
      event
    );
    backgroundColor =
      backgroundFromSettings || event.resource?.lessonTypeColor || defaultColor;
  }

  // Get border color from settings source
  const borderColor =
    displaySettings.borderColorSource !== 'none'
      ? getColorForSource(displaySettings.borderColorSource, event)
      : undefined;

  // Determine text color based on background brightness
  const textColor = getContrastTextColor(backgroundColor);

  // Rescheduled occurrences get a dashed outline visual indicator
  // (uses outline to avoid overriding user-configured border color)
  const isRescheduled = event.resource?.isRescheduled === true;
  const isCancelled = event.resource?.isCancelled === true;
  const isAbsenteeReleased = event.resource?.isAbsenteeReleased === true;

  const resolvedBackgroundColor = isAbsenteeReleased ? '#9DA5AF' : backgroundColor;
  const resolvedOpacity = isPlaceholder || isAbsenteeReleased ? 0.5 : isCancelled ? 0.6 : 1;
  const resolvedOutline = isRescheduled
    ? '2px dashed #ff9800'
    : isCancelled
    ? '2px solid #d32f2f'
    : isAbsenteeReleased
    ? '2px dashed #9DA5AF'
    : undefined;

  return {
    style: {
      backgroundColor: resolvedBackgroundColor,
      borderRadius: '4px',
      opacity: resolvedOpacity,
      color: isAbsenteeReleased ? '#fff' : textColor,
      border: borderColor ? `2px solid ${borderColor}` : 'none',
      outline: resolvedOutline,
      outlineOffset: isRescheduled || isCancelled || isAbsenteeReleased ? '-2px' : undefined,
      fontSize: '12px',
      padding: '2px 4px',
      cursor: isDraggable ? 'grab' : 'pointer',
      textDecoration: isCancelled ? 'line-through' : undefined,
    },
  };
};

/**
 * Check if an event is draggable.
 *
 * A CANCELLED occurrence is never draggable: the backend rejects a reschedule of one
 * ("This occurrence is cancelled. Restore it before rescheduling." — 409) and there is
 * no restore UI yet, so dragging it is a dead end. Cancelled events still RENDER (red
 * outline + strikethrough), so without this they stay grabbable. Mirrors the context
 * menu, which hides "Cancel this class" on the same events.
 */
export const isEventDraggable = (event: CalendarEvent): boolean => {
  if (event.resource?.isCancelled === true) return false;
  const type = event.resource?.type;
  return type ? DRAGGABLE_TYPES.includes(type) : false;
};
